import { getWorkerImg } from "services/firebase/firebase.service";
import {
  CompanyGigApplicationWorker,
  CompanyGigShift,
  GigCosts,
  GigType,
  PaymentMethod,
} from "model/Gig";
import { TimeReportDto, TimeReportResolution } from "model/TimeReport";
import { Api } from "services/api/api.service";
import moment from "moment";
import i18n from "services/translation/i18n";

export const getWorkerImages = async (
  workerIds: Array<CompanyGigApplicationWorker["externalId"]>
): Promise<string[]> => {
  let arr = [];

  for (const id of workerIds) {
    const url = await getWorkerImg(id!);

    if (url) {
      arr.push(url);
    } else {
      arr.push("no-image");
    }
  }

  return arr;
};

export const validateEmail = (email: string) => {
  const re = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

  if (email && !re.test(email)) {
    return false;
  } else {
    return true;
  }
};

export const formatWorkerDateOfBirth = (birthString: string) => {
  const year = birthString.slice(0, 4);
  const month = birthString.slice(4, 6);
  const day = birthString.slice(6, 8);
  const workerDate = `${year}-${month}-${day}`;
  return workerDate;
};

/**
  Clamp a given value between a min and max number
 */
export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const dateDiff = (a: Date, b: Date, divider = 1) =>
  Math.abs(b.getTime() - a.getTime()) / divider;

export const shiftWithAbsence = (timeReport: any) => {
  return (
    timeReport.resolution == TimeReportResolution.SICKLEAVE ||
    timeReport.resolution == TimeReportResolution.SICKLEAVECHILD ||
    timeReport.resolution == TimeReportResolution.VACATION ||
    timeReport.resolution == TimeReportResolution.PARENTALLEAVE ||
    timeReport.resolution == TimeReportResolution.LEAVEOFABSENCE ||
    timeReport.resolution == TimeReportResolution.OVERTIMECOMPENSATION ||
    timeReport.resolution == TimeReportResolution.FUNERALLEAVE ||
    timeReport.resolution == TimeReportResolution.MILITARYSERVICE ||
    timeReport.resolution == TimeReportResolution.WITHDRAWN ||
    timeReport.resolution == TimeReportResolution.CANCELLED ||
    timeReport.resolution == TimeReportResolution.OTHER
  );
};

export const getCosts = async (
  shifts: CompanyGigShift[],
  minHourlyRate: number,
  maxHourlyRate: number,
  gigType: GigType | undefined, //hääär
  companyId: number,
  paymentType: PaymentMethod | undefined
): Promise<{ minCost: GigCosts; maxCost: GigCosts }> => {
  const shiftsMin = shifts.map((shift) => {
    let breakStartTime = shift.breakStartTime;
    let breakEndTime = shift.breakEndTime;
    if (shift.breakTime) {
      breakEndTime = moment(shift.startTime)
        .add("minutes", shift.breakTime)
        .format("YYYY-MM-DDTHH:mm");
      breakStartTime = moment(shift.startTime).format("YYYY-MM-DDTHH:mm");
    }

    return {
      breakStartTime: breakStartTime,
      breakEndTime: breakEndTime,
      startTime: shift.startTime,
      endTime: shift.endTime,
      companyId: companyId,
      gigType: gigType,
      hourlyRate: minHourlyRate,
      workerId: null,
      paymentType: paymentType,
    };
  });

  const shiftsMax = shifts.map((shift) => {
    let breakStartTime = shift.breakStartTime;
    let breakEndTime = shift.breakEndTime;
    if (shift.breakTime) {
      breakEndTime = moment(shift.startTime)
        .add("minutes", shift.breakTime)
        .format("YYYY-MM-DDTHH:mm");
      breakStartTime = moment(shift.startTime).format("YYYY-MM-DDTHH:mm");
    }
    return {
      breakStartTime: breakStartTime,
      breakEndTime: breakEndTime,
      startTime: shift.startTime,
      endTime: shift.endTime,
      companyId: companyId,
      gigType: gigType,
      hourlyRate: maxHourlyRate,
      workerId: null,
      paymentType: paymentType,
    };
  });

  const minCostPromise = Api().company.gig.getGigCost(companyId, shiftsMin);
  const maxCostPromise = Api().company.gig.getGigCost(companyId, shiftsMax);

  const [minCostResponse, maxCostResponse] = await Promise.all([
    minCostPromise,
    maxCostPromise,
  ]);
  const minCost = minCostResponse;
  const maxCost = maxCostResponse;
  // @ts-ignore: Unreachable code error
  return { minCost, maxCost };
};

export const getCostsTimeReports = async (
  timeReports: TimeReportDto[],
  companyId: number
): Promise<GigCosts> => {
  const costsDto = timeReports.map((timeReport) => ({
    breakStartTime: timeReport.breakStartTime,
    breakEndTime: timeReport.breakEndTime,
    startTime: timeReport.startTime,
    endTime: timeReport.endTime,
    companyId: timeReport.companyId,
    gigType: timeReport.gigType,
    hourlyRate: timeReport.hourlyRate,
    workerId: timeReport.workerId,
    paymentType: timeReport.paymentType,
  }));

  return Api()
    .company.gig.getGigCost(companyId, costsDto)
    .then((costs) => costs)
    .catch((err) => err);
};

export const fetchWorker = async (workerId: number, companyState: any) => {
  if (companyState.company?.id) {
    return Api()
      .company.getWorkerDetailsByWorkerId(companyState.company?.id, workerId)
      .then((worker) => worker)
      .catch((err) => err);
  }
};

export const getMonthNameFromPeriod = (periodCode: string): string => {
  const lang = i18n.language;
  let language: "en" | "sv" = lang.includes("sv") ? "sv" : "en";

  const monthNumber = parseInt(periodCode.slice(4, 6), 10);

  if (monthNumber < 1 || monthNumber > 12 || isNaN(monthNumber)) {
    throw new Error(`Invalid month number: ${monthNumber}`);
  }

  const monthNames: Record<"en" | "sv", string[]> = {
    en: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    sv: [
      "Januari",
      "Februari",
      "Mars",
      "April",
      "Maj",
      "Juni",
      "Juli",
      "Augusti",
      "September",
      "Oktober",
      "November",
      "December",
    ],
  };

  return monthNames[language][monthNumber - 1];
};
